import { useState, ChangeEvent, useEffect } from 'react';
import { Button, Paper, Container, Typography, Autocomplete, TextField, InputAdornment, Snackbar, Alert, Chip } from '@mui/material';
import { MoneyOff, FontDownloadOffOutlined, PhonelinkEraseOutlined } from '@mui/icons-material';

import Grid from '@mui/material/Unstable_Grid2';
import countries from './db/countries.json';
import getCountryCode from './utils/getCountryCode';

interface ICountry {
  name: string;
  flag: string;
  code: string;
  dial_code: string;
}

interface AppProps {
  appUpdatePending: boolean;
  updateAction: () => void;
}

const validateInitialState = {
  error: false,
  errorText: '',
};

const KEY_LOCAL_STORAGE = 'selected-country';

function getInitialCountry() {
  const memorySelectedCountry = window.localStorage.getItem(KEY_LOCAL_STORAGE);

  if (memorySelectedCountry) {
    return JSON.parse(memorySelectedCountry);
  }

  const defaultCountry = countries[101];

  const cCode = getCountryCode();

  if (!cCode) {
    return defaultCountry;
  }

  const matchCountry = countries.find(country => country.code === cCode);

  return matchCountry || defaultCountry;
}

function App(props: AppProps) {
  const { appUpdatePending, updateAction } = props;
  const [showUpdateBtn, setShowUpdateBtn] = useState(appUpdatePending);

  const [selectedCountry, setSelectedCountry] = useState<ICountry>(getInitialCountry());
  const [phoneNumber, setPhoneNumber] = useState('');
  const [validate, setValidate] = useState(validateInitialState);

  useEffect(() => {
    setShowUpdateBtn(appUpdatePending);
  }, [appUpdatePending]);

  const handleStartChat = () => {
    if (!phoneNumber.length) {
      setValidate({ error: true, errorText: 'Phone Number is Required' });
      return;
    }

    window.location.assign(`https://wa.me/${selectedCountry.dial_code.slice(1)}${phoneNumber}`);
  };

  const handleChangePhoneNumber = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setValidate(validateInitialState);
    let phoneInput = e.target.value.replace(/\D/g, '');

    const countryDialCode = selectedCountry.dial_code.slice(1);

    if (phoneInput.startsWith(countryDialCode)) {
      phoneInput = phoneInput.slice(countryDialCode.length);
    }

    phoneInput = phoneInput.replace(/^0+/, '');
    setPhoneNumber(phoneInput);
  };

  const handleSelectCountry = (country: ICountry) => {
    setSelectedCountry(country);
    window.localStorage.setItem(KEY_LOCAL_STORAGE, JSON.stringify(country));
  };

  return (
    <Container>
      <Snackbar
        open={showUpdateBtn}
        anchorOrigin={{ vertical: 'top', horizontal: 'center'  }}
      >
        <Alert
          severity="info"
          variant="outlined"
          action={
            <Button size="small" color="inherit" onClick={updateAction}>Update</Button>
          }
        >
          New Version Available
        </Alert>
      </Snackbar>

      <Paper sx={{ mt: 5, mb: 1, p: 5 }} elevation={24}>
        <Grid container justifyContent="center" spacing={2}>
          <Grid xs={12}>
            <Typography align="center" mb={2}>
              <img src="/InstantWApp-logo.png" width={70} height={70} alt="quick-whatsapp logo" /> <br />
              Tired of saving numbers just to shoot a quick message on WhatsApp? <br />
              Say goodbye to that hassle.
            </Typography>
          </Grid>
          <Grid xs={12} md={4}>
            <Autocomplete
              disablePortal
              value={selectedCountry}
              onChange={(_, newValue) => {
                handleSelectCountry(newValue);
              }}
              disableClearable
              id="country-select"
              options={countries}
              getOptionLabel={(option) => `${option.flag} ${option.name}`}
              isOptionEqualToValue={(opt, val) => opt.code === val.code}
              renderInput={(params) => <TextField {...params} label="Search & Select Country" />}
            />
          </Grid>
          <Grid>
            <TextField
              required
              autoFocus
              inputProps={{ inputMode: 'numeric' }}
              label="Phone Number"
              id="phone-number-input"
              error={validate.error}
              value={phoneNumber}
              onChange={handleChangePhoneNumber}
              onKeyUp={(e) => {
                if (e.key === 'Enter') {
                  handleStartChat();
                }
              }}
              InputProps={{
                startAdornment: <InputAdornment position="start">{selectedCountry.dial_code}</InputAdornment>,
              }}
              helperText={validate.errorText}
            />
          </Grid>
          <Grid>
            <Button variant="contained" color="success" onClick={handleStartChat} size="large" startIcon={<img src="/InstantWApp-logo.png" width={30} height={30} alt="quick-whatsapp logo" />}>
              Start Chat
            </Button>
          </Grid>
        </Grid>
      </Paper>
      <Grid container spacing={1} mt={1}>
        <Grid>
          <Chip variant="outlined" color="warning" icon={<PhonelinkEraseOutlined />} label="NO SAVE NUMBER" />
        </Grid>
        <Grid>
          <Chip variant="outlined" color="error" label="NO ADS" icon={<FontDownloadOffOutlined />} />
        </Grid>
        <Grid>
          <Chip variant="outlined" color="success" label="FREE" icon={<MoneyOff />} />
        </Grid>
      </Grid>
    </Container>
  );
}

export default App;
